import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import '../styles/HRDashboard.css';
import { ChartGuideModal } from '../components/ui';
import LicenseGate from '../components/ui/LicenseGate';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import useChartGuide from '../hooks/useChartGuide';
import { CHART_GUIDES } from '../constants/chartGuides';
import { useBranches } from '../hooks/useBranches';
import { logger } from '../utils/logger';
import {
  KPIsSection,
  PersonnelPerformanceDistribution,
  TeamComposition,
  TopPersonnelPerformance,
  KeyPerformanceMetrics,
  ComparisonPerformanceByRole,
  PersonnelPerformanceByRole
} from '../components/hr';

interface HRDashboardProps {
  selectedBrand?: any;
  selectedBranch?: any;
  selectedPeriod?: string;
}

const HRDashboard: React.FC<HRDashboardProps> = ({
  selectedBrand,
  selectedBranch,
  selectedPeriod
}) => {
  const { currentTheme } = useTheme();
  const { timeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { selectedBranch: branchFromContext } = useBranches(1);
  
  // Use the provided branch or the one from context
  const effectiveBranch = selectedBranch || branchFromContext;
  
  // Error handler
  const handleError = (error: Error) => {
    logger.error('HR Dashboard Error', 'HRDashboard', error);
  };
  
  // If no branch is selected, show a message
  if (!effectiveBranch) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="card-base card-padding border border-status-error">
          <div className="text-center py-8 sm:py-12" role="alert">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="title-card">برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.</h3>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">برای مشاهده داشبورد منابع انسانی، ابتدا باید یک شعبه را انتخاب کنید</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LicenseGate licenseType="evaluation" dashboardName="تحلیل منابع انسانی">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Header Section */}
          <h1 className="title-dashboard">
            تحلیل منابع انسانی — یک ماه اخیر
          </h1>
          <p className="text-text-secondary text-xs sm:text-sm lg:text-base mb-6 text-center">
            ارزیابی عملکرد پرسنل و تحلیل کیفیت خدمات
          </p>

          {/* Key Performance Indicators */}
          <h2 className="title-section">شاخص‌های کلیدی عملکرد پرسنل</h2>
     
        
          <div className="card-base card-padding animate-enter animate-delay-3">
            <h3 className="title-card">عملکرد کلی پرسنل</h3>
            <DashboardSectionAnchor sectionId="hr-kpis">
              <KPIsSection 
                branchId={effectiveBranch?.id || effectiveBranch}
                onError={handleError}
              />
            </DashboardSectionAnchor>
          </div>
          

          {/* Performance Distribution and Team Composition */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <DashboardSectionAnchor sectionId="personnel-performance-distribution">
              <PersonnelPerformanceDistribution 
                branchId={effectiveBranch?.id || effectiveBranch}
                onError={handleError}
              />
            </DashboardSectionAnchor>
            <DashboardSectionAnchor sectionId="team-composition">
              <TeamComposition 
                branchId={effectiveBranch?.id || effectiveBranch}
                onError={handleError}
              />
            </DashboardSectionAnchor>
          </div>

          <DashboardSectionAnchor sectionId="top-personnel-performance">
            <TopPersonnelPerformance 
              branchId={effectiveBranch?.id || effectiveBranch}
              onError={handleError}
            />
          </DashboardSectionAnchor>

          {/* Key Metrics and Comparison by Role */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DashboardSectionAnchor sectionId="key-performance-metrics">
              <KeyPerformanceMetrics 
                branchId={effectiveBranch?.id || effectiveBranch}
                onError={handleError}
              />
            </DashboardSectionAnchor>
            <DashboardSectionAnchor sectionId="comparison-performance-by-role">
              <ComparisonPerformanceByRole 
                branchId={effectiveBranch?.id || effectiveBranch}
                onError={handleError}
              />
            </DashboardSectionAnchor>
          </div>

          <DashboardSectionAnchor sectionId="personnel-performance-by-role">
            <PersonnelPerformanceByRole 
              branchId={effectiveBranch?.id || effectiveBranch}
              onError={handleError}
            />
          </DashboardSectionAnchor>
        </div>

        {currentGuide && (
          <ChartGuideModal
            isOpen={isModalOpen}
            onClose={closeGuide}
            title={currentGuide.title}
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                <p className="text-text-secondary">{currentGuide.description}</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                <p className="text-text-secondary">{currentGuide.interpretation}</p>
              </div>
              {currentGuide.tips.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentGuide.tips.map((tip, index) => (
                      <li key={index} className="text-text-secondary">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ChartGuideModal>
        )}
      </div>
    </LicenseGate>
  );
};

export default HRDashboard;